/* eslint-disable @repo/internal/react/require-jsdoc */
import React, { useEffect } from 'react';

import type { AllSearchResults, BasicDoc, StateResultsProvided } from 'react-instantsearch-core';
import { connectStateResults } from 'react-instantsearch-dom';

import { HeadingIcon } from '@af/design-system-docs-ui';
import { LinkButton } from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { ContextualAnalyticsData, FireScreenAnalytics } from '@atlassian/analytics-bridge';

import noResultsImg from '../../images/no-results.png';
import { sortHitsByStatus } from '../../utils/search';

import { type Indices } from './client';
import ResultItem from './result-item';

/**
 * The connectStateResults function, from `react-instantsearch-dom`, takes a callback that gets passed a `searching` boolean.
 * However, this boolean is set to `false` early, while the `allSearchResults` object is only partially populated with results.
 * This is by design, for searches that would take a long time. For our use case, this results in janky UX. So, isSearching()
 * is a slightly hacky alternative to determine when the search is actually complete.
 *
 * @param {string} query The initial search query.
 * @param {AllSearchResultsType} allSearchResults An object containing all search results, grouped by section.
 * @returns {boolean} Whether there exists at least one result section where the query does not match the original search query.
 */
const isSearching = (query?: string, allSearchResults?: AllSearchResults<BasicDoc>): boolean => {
	if (!allSearchResults) {
		return false;
	}

	return Object.values(allSearchResults).some((resultSection) => resultSection.query !== query);
};

const loadingStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flexGrow: '1',
});

const resultsContainerStyles = xcss({
	marginInlineStart: 'space.300',
	marginInlineEnd: 'space.200',
	paddingBlockStart: 'space.050',
	borderInlineStartWidth: 'border.width',
	borderInlineStartStyle: `solid`,
	borderImage: `linear-gradient(to bottom, ${token('color.border')}, ${token('elevation.surface.overlay')}) 1 100%`,
});

const headingItemStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	paddingBlock: 'space.150',
	paddingInline: 'space.150',
	marginBlock: 'space.0',
	marginInline: 'space.200',
	borderRadius: 'radius.large',
	gap: 'space.100',
});

const backgroundMapping: Record<Indices, string> = {
	Components: token('color.background.accent.green.subtlest'),
	Tokens: token('color.background.accent.yellow.subtlest'),
	Foundations: token('color.background.accent.teal.subtlest'),
	'Get started': token('color.background.accent.purple.subtlest'),
	Patterns: token('color.background.accent.orange.subtlest'),
};

type ResultsProps = {
	onResultClick?(): void;
	activeIndex: number;
	onResultsCountChange(count: number): void;
	onSearchingChange?(isSearching: boolean): void;
	listboxId: string;
	shouldShowDefaultResults?: boolean;
	resultRef?: (index: number) => (el: HTMLAnchorElement | null) => void;
} & StateResultsProvided<BasicDoc>;

/**
 * __Results__
 */

const Results = connectStateResults<ResultsProps>(
	({
		onResultClick,
		searchState: search,
		allSearchResults,
		activeIndex,
		onResultsCountChange,
		onSearchingChange,
		listboxId,
		shouldShowDefaultResults,
		resultRef,
	}) => {
		const query = search.query || '';
		const isQueryEmpty = query === '';
		// When showing default results, treat empty query as if it has results
		const searching = !isQueryEmpty && isSearching(query, allSearchResults);

		const results = allSearchResults
			? Object.entries(allSearchResults).filter(([, searchResults]) => {
					// During instantsearch updates, sections can be partially populated.
					return Array.isArray(searchResults?.hits) && searchResults.hits.length > 0;
				})
			: [];

		const totalHits = results.reduce((sum, [, searchResults]) => {
			return sum + (Array.isArray(searchResults?.hits) ? searchResults.hits.length : 0);
		}, 0);

		// Notify parent of searching state changes
		useEffect(() => {
			onSearchingChange?.(searching);
		}, [searching, onSearchingChange]);

		useEffect(() => {
			// Only publish a stable results count once searching has completed.
			// While searching, InstantSearch can provide partially-populated results which causes keyboard nav to feel "skippy".
			if (isQueryEmpty && !shouldShowDefaultResults) {
				onResultsCountChange(0);
				return;
			}
			if (!searching) {
				onResultsCountChange(totalHits);
			}
		}, [isQueryEmpty, searching, onResultsCountChange, totalHits, shouldShowDefaultResults]);

		// Show results if we have a query OR if we're showing default results
		if (isQueryEmpty && !shouldShowDefaultResults) {
			return null;
		}

		if (searching) {
			return (
				<Box xcss={loadingStyles} paddingBlock="space.200">
					<Spinner />
				</Box>
			);
		}

		const renderedResults = results.length ? (
			(() => {
				let globalIndex = 0;
				return results.map(([indexName, searchResults]) => {
					const sectionTitleId = `${indexName}-title`;
					return (
						<Box key={indexName}>
							{/* Visual heading that doesn't interfere with listbox semantics */}
							<Box
								xcss={headingItemStyles}
								style={{
									backgroundColor: backgroundMapping[indexName as Indices],
								}}
								role="presentation"
								aria-hidden="true"
							>
								<HeadingIcon heading={indexName as Indices} />
								<Heading size="small" as="h2" id={sectionTitleId} aria-hidden="true">
									{indexName}
								</Heading>
							</Box>
							<Box xcss={resultsContainerStyles}>
								{sortHitsByStatus(Array.isArray(searchResults?.hits) ? searchResults.hits : []).map(
									(hit: any) => {
										const resultIndex = globalIndex;
										globalIndex += 1;
										return (
											<ResultItem
												indexName={indexName as Indices}
												key={`${indexName}--${hit.id}`}
												hit={hit}
												onClick={onResultClick}
												resultIndex={resultIndex}
												posInSet={resultIndex + 1}
												setSize={totalHits}
												isKeyboardActive={resultIndex === activeIndex}
												listboxId={listboxId}
												queryKey={query}
												resultRef={resultRef?.(resultIndex)}
											/>
										);
									},
								)}
							</Box>
						</Box>
					);
				});
			})()
		) : (
			<EmptyState
				imageUrl={noResultsImg}
				header={`We can't find anything for "${query}".`}
				description="Try a different term or browse our components."
				primaryAction={<LinkButton href="/components">Browse components</LinkButton>}
			/>
		);

		return (
			<ContextualAnalyticsData
				attributes={{
					resultsCount: results ? results.length : 0,
				}}
			>
				<FireScreenAnalytics key={search.query} />
				{renderedResults}
			</ContextualAnalyticsData>
		);
	},
);

export default Results;
